import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useEffectOnce, useObservable } from '@paradigm/utils/src/hooks';

import UnifiedOpenOrdersService from '#/unified-rfqs/services/unified-open-orders';
import UnifiedOrdersService from '#/unified-rfqs/services/unified-orders';
import { Order, isOpenOrder } from '#/unified-rfqs/entities/shared';

import { useGrfqService } from '#/unified-rfqs/contexts/grfqs';
import { useDrfqService } from '#/unified-rfqs/contexts/drfqs';
import OrdersStore from '#/unified-rfqs/store/orders';
import createSafeContext from '@paradigm/utils/createSafeContext';
import { FailedResp } from '@paradigm/api/src';
import { useWsStatus } from './use-ws-status';

const [useUnifiedOrdersService, UnifiedOrdersServiceProviderCtx] =
  createSafeContext<UnifiedOrdersService>('UnifiedOrdersService');

interface Props {
  readonly children: ReactNode;
  readonly ordersStore: OrdersStore;
}

export { useUnifiedOrdersService };
export function UnifiedOrdersServiceProvider({ children, ordersStore }: Props) {
  const grfqService = useGrfqService();
  const drfqService = useDrfqService();

  const ordersService = useMemo(
    () =>
      new UnifiedOrdersService({
        grfqService,
        drfqService,
        ordersStore,
      }),
    [grfqService, drfqService, ordersStore],
  );

  return (
    <UnifiedOrdersServiceProviderCtx
      key={ordersService.id}
      value={ordersService}
    >
      {children}
    </UnifiedOrdersServiceProviderCtx>
  );
}

const [useUnifiedOpenOrdersService, UnifiedOpenOrdersServiceProviderCtx] =
  createSafeContext<UnifiedOpenOrdersService>('UnifiedOpenOrdersService');

interface Props {
  readonly children: ReactNode;
  readonly ordersStore: OrdersStore;
}

export { useUnifiedOpenOrdersService };

export const [
  useUnifiedOpenOrdersInitialLoadStatus,
  UnifiedOpenOrdersInitialLoadStatusProviderCtx,
] = createSafeContext<{
  error: string;
  isLoadingLatest: boolean;
}>('UnifiedOpenOrdersService');

export function UnifiedOpenOrdersServiceProvider({
  children,
  ordersStore,
}: Props) {
  const grfqService = useGrfqService();
  const drfqService = useDrfqService();

  const ordersService = useMemo(
    () =>
      new UnifiedOpenOrdersService({
        grfqService,
        drfqService,
        ordersStore,
      }),
    [grfqService, drfqService, ordersStore],
  );

  const { status } = useObservable(ordersService.fetcher.fetchData);
  const [fetchError, setFetchError] = useState('');
  const { error: wsError, subscribed, subscribing, connected } = useWsStatus();
  useEffect(() => {
    if (!subscribed) return;
    setFetchError('');
    ordersService.fetcher.fetchLatest({}, (resp: FailedResp) => {
      setFetchError(resp.message);
    });
    // Fetch quotes once subscribed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribed]);
  const error = fetchError.length > 0 ? fetchError : wsError;
  const isLoadingLatest =
    !connected || subscribing || status === 'fetching-latest';

  return (
    <UnifiedOpenOrdersServiceProviderCtx
      key={ordersService.id}
      value={ordersService}
    >
      <UnifiedOpenOrdersInitialLoadStatusProviderCtx
        value={{
          error,
          isLoadingLatest,
        }}
      >
        {children}
      </UnifiedOpenOrdersInitialLoadStatusProviderCtx>
    </UnifiedOpenOrdersServiceProviderCtx>
  );
}

export function useUnifiedOpenOrdersFetchData() {
  const openOrdersService = useUnifiedOpenOrdersService();
  return useObservable(openOrdersService.fetcher.fetchData);
}

export function useUnifiedOpenOrdersIdsList() {
  const openOrdersService = useUnifiedOpenOrdersService();
  const [quoteIds, setQuotesIds] = useState(
    openOrdersService.fetcher.selectPaginatedIds(),
  );

  useEffectOnce(() => {
    return openOrdersService.fetcher.subscribePaginatedIds(setQuotesIds);
  });

  return quoteIds;
}

export function useUnifiedOrdersFetchData() {
  const ordersService = useUnifiedOrdersService();
  return useObservable(ordersService.fetcher.fetchData);
}

export function useOrdersIdsList() {
  const ordersService = useUnifiedOrdersService();
  const [quoteIds, setQuotesIds] = useState(
    ordersService.fetcher.selectPaginatedIds(),
  );

  useEffectOnce(() => {
    return ordersService.fetcher.subscribePaginatedIds(setQuotesIds);
  });

  return quoteIds;
}

export function useMaybeOrder(orderId: Order['id']) {
  const svc = useUnifiedOrdersService();
  const ob = useMemo(() => svc.item(orderId), [orderId, svc]);
  return useObservable(ob);
}

/**
 * ATTENTION: This hook should be used only in places in which
 * the order is guaranteed to exist.
 *
 */
export function useOrder(orderId: Order['id']) {
  const order = useMaybeOrder(orderId);

  if (order == null) {
    throw new TypeError(`Order ${orderId} is null`);
  }

  return order;
}

export function useUnifiedOpenOrdersCount() {
  const { list: listOrders } = useUnifiedOpenOrdersService();
  const orders = useObservable(listOrders());
  return orders.filter(isOpenOrder).length;
}
